import { Link } from 'react-router-dom';
import { useMyPoints, useMyRank, usePublicGamificationSettings } from '@/hooks';
import { Zap, Star, TrendingUp, Flame, ChevronRight } from 'lucide-react';
import { FogoVivo } from '@/components/gamification/IconesVivos';
import {
  sequenciaViva,
  useMinhaSequencia,
  useVerSequencia,
} from '@/hooks/useSequencia';

interface GamificationWidgetProps {
  compact?: boolean;
}

export function GamificationWidget({
  compact = false,
}: GamificationWidgetProps) {
  const { data: myPoints, isLoading } = useMyPoints();
  const { data: myRank } = useMyRank();
  const { data: publicSettings } = usePublicGamificationSettings();
  // a sequência mora em /streaks/me (points/me não traz esse número)
  const { data: sequencia } = useMinhaSequencia();
  const verSequencia = useVerSequencia();

  const settingsData = publicSettings;
  if (!settingsData?.enabled) return null;

  const pointsData = myPoints;
  const rankData = myRank;

  const totalPoints = pointsData?.totalPoints || 0;
  const currentLevel = pointsData?.level;
  const nextLevel = currentLevel?.nextLevel;
  const streak = sequenciaViva(sequencia);
  const rank = rankData?.rank;

  const progressToNext = nextLevel
    ? Math.min(
        100,
        Math.round(
          ((totalPoints - (currentLevel?.minPoints || 0)) /
            ((nextLevel.minPoints || nextLevel.pointsNeeded || 1) -
              (currentLevel?.minPoints || 0))) *
            100,
        ),
      )
    : 100;

  const levelColor = currentLevel?.color || '#A3E635';

  if (isLoading) {
    if (compact) {
      return (
        <div className="border-border/50 border-b px-4 py-2.5">
          <div className="flex animate-pulse items-center gap-2">
            <div className="bg-muted h-7 w-7 rounded-lg" />
            <div className="bg-muted h-4 w-20 rounded" />
            <div className="bg-muted ml-auto h-4 w-12 rounded" />
          </div>
        </div>
      );
    }
    return (
      <div className="border-border/50 border-b px-4 py-5">
        <div className="animate-pulse space-y-3">
          <div className="bg-muted h-5 w-24 rounded-lg" />
          <div className="bg-muted h-2 w-full rounded-full" />
          <div className="flex gap-3">
            <div className="bg-muted h-8 w-16 rounded-lg" />
            <div className="bg-muted h-8 w-16 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  // Compact mode: single row with level, XP, rank, streak
  if (compact) {
    return (
      <Link
        to="/ranking"
        className="border-border/50 hover:bg-secondary/30 group flex items-center gap-2 border-b px-4 py-2.5 transition-all duration-200"
      >
        <div
          className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{
            background: `linear-gradient(135deg, ${levelColor}30, ${levelColor}10)`,
            border: `1px solid ${levelColor}40`,
          }}
        >
          <Star className="h-3.5 w-3.5" style={{ color: levelColor }} />
        </div>
        <span
          className="truncate text-xs font-bold"
          style={{ color: levelColor }}
        >
          {currentLevel?.name || 'Iniciante'}
        </span>
        {nextLevel && (
          <div className="mx-1 flex-1">
            <div className="bg-secondary/80 h-1.5 overflow-hidden rounded-full">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progressToNext}%`,
                  background: `linear-gradient(90deg, ${levelColor}, ${levelColor}CC)`,
                }}
              />
            </div>
          </div>
        )}
        <div className="flex shrink-0 items-center gap-1.5">
          <div
            className="flex items-center gap-0.5 text-xs font-bold"
            style={{ color: levelColor }}
          >
            <Zap className="h-3 w-3" />
            {totalPoints.toLocaleString()}
          </div>
          {rank && (
            <span className="text-xs font-bold text-purple-400">#{rank}</span>
          )}
          {streak > 0 && (
            <span className="text-xs font-bold text-orange-400">
              <Flame className="inline h-3 w-3" />
              {streak}d
            </span>
          )}
        </div>
        <ChevronRight className="text-muted-foreground/50 group-hover:text-primary/60 h-3 w-3 shrink-0 transition-colors" />
      </Link>
    );
  }

  return (
    <div className="border-border/50 hover:bg-secondary/10 group border-b px-3 py-3 transition-colors duration-200">
      {/* Level Header */}
      <div className="mb-2.5 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
            style={{
              background: `linear-gradient(135deg, ${levelColor}30, ${levelColor}10)`,
              border: `1px solid ${levelColor}40`,
            }}
          >
            <Star className="h-4.5 w-4.5" style={{ color: levelColor }} />

            <div
              className="border-background absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2"
              style={{ backgroundColor: levelColor }}
            />
          </div>

          <span
            className="truncate text-sm font-bold"
            style={{ color: levelColor }}
          >
            {currentLevel?.name || 'Iniciante'}
          </span>
        </div>

        <div
          className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold"
          style={{
            background: `linear-gradient(135deg, ${levelColor}20, ${levelColor}08)`,
            color: levelColor,
          }}
        >
          <Zap className="h-3.5 w-3.5" />
          {totalPoints.toLocaleString()}
        </div>
      </div>

      {/* Progress */}
      {nextLevel && (
        <div className="mb-2.5">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
              Progresso
            </span>

            <span className="text-xs font-bold" style={{ color: levelColor }}>
              {progressToNext}%
            </span>
          </div>

          <div className="bg-secondary/80 h-2 overflow-hidden rounded-full">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${progressToNext}%`,
                background: `linear-gradient(90deg, ${levelColor}, ${levelColor}CC)`,
              }}
            />
          </div>

          <p className="text-muted-foreground mt-1 text-xs">
            {(nextLevel.minPoints ||
              (currentLevel?.minPoints || 0) + nextLevel.pointsNeeded) -
              totalPoints}{' '}
            XP para{' '}
            <span
              className="font-semibold"
              style={{ color: ('color' in nextLevel && typeof nextLevel.color === 'string' ? nextLevel.color : levelColor) }}
            >
              {nextLevel.name}
            </span>
          </p>
        </div>
      )}

      {/* Bottom row */}
      <div className="flex items-center gap-2">
        {rank && (
          <div className="flex items-center gap-1.5 rounded-md bg-purple-500/10 px-2 py-1">
            <TrendingUp className="h-3.5 w-3.5 text-purple-400" />

            <span className="text-xs font-bold text-purple-400">#{rank}</span>
          </div>
        )}

        {streak > 0 && (
          // tocar mostra o fogo da sequência
          <button
            type="button"
            onClick={() => verSequencia(sequencia)}
            aria-label={`Sequência de ${streak} dias: ver o fogo`}
            className="flex items-center gap-1.5 rounded-md bg-orange-500/10 px-2 py-1 transition-colors hover:bg-orange-500/20 active:scale-95"
          >
            <FogoVivo className="h-3.5 w-3.5" />
            <span className="text-xs font-bold text-orange-400">{streak}d</span>
          </button>
        )}

        <Link
          to="/ranking"
          className="text-muted-foreground hover:text-primary ml-auto flex items-center gap-0.5 text-xs font-medium transition-colors"
        >
          <span>Ranking</span>

          <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
    </div>
  );
}
